import React, { useCallback, useMemo, useRef, useState } from 'react';

import {
  COMPONENT_VARIANTS,
  CreateProjectModal,
  CreateProjectSubmitResponse,
  CreateProjectSubmitSuccess,
} from '@adsk/acs-admin-ui-components';
import { track } from '@adsk/acs-web-analytics';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import Button from '@adsk/alloy-react-button';
import { PlusIcon } from '@adsk/alloy-react-icon';
import { useNotifications } from '@adsk/alloy-react-notification';
import { TOTAL_REMOTE_LOADING_TIME } from '@adsk/exo-skeleton-api';
import { TabTitle } from '@adsk/exo-skeleton-react';

import {
  AnalyticsEvents,
  ErrorNotificationBaseProps,
  FEATURE_FLAGS,
  MAPBOX_ACCESS_TOKEN,
} from '../../constants';
import i18n from '../../helpers/i18n';
import { trackRowClick } from '../../helpers/trackRowClick';
import { usePageStates } from '../../helpers/usePageStates';
import { useFilters } from '../../hooks/useFilters';
import usePerformanceTrackingWithEffects from '../../hooks/usePerformanceTrackingWithEffects';
import { useProjectsQuery } from '../../hooks/useProjectsQuery';
import { getProjectCreateErrorMessage } from '../../utils/getProjectCreateErrorMessage';
import { useSetProductEntryFlow } from '../Contexts/ProductEntryFlowContext';
import ProjectListEmptyState from '../EmptyStates/ProjectListEmptyState';
import HttpErrorState from '../HttpErrorState';
import { PageWithSearchAndFilters } from '../layout/PageWithSearchAndFilters';
import { ProjectListErrorBoundery } from '../ProjectListErrorBoundery';
import { scrollToTop } from '../ScrollContainer';
import {
  accountNameCellColumn,
  createdAtCellColumn,
  jobNumberColumn,
  productEntryFlowCellColumn,
  nameColumn,
  platformColumn,
  defaultAccessCellColumn,
} from '../Table/Columns';
import { SearchBar } from '../Table/SearchBar';
import { Table } from '../Table/Table';
import { TableLink } from '../Table/TableLink';

const PROJECT_LIST_TRACKING_ID = 'project-list';

const ProjectList = () => {
  const { value: toolEntryFlow } = useAlloyFeatureFlag(
    FEATURE_FLAGS.ToolEntryFlow,
  );
  const { showNotification } = useNotifications();
  const modalOpenTime = useRef<number>(0);
  const shouldReportView = useRef<boolean>(false);
  const [showProjectCreateModal, setShowProjectCreateModal] = useState(false);

  const {
    search,
    sorting,
    pagination,
    filters,
    tableProps,
    sideFiltersProps,
    onSearchChange,
  } = useFilters({
    onPaginationChange: scrollToTop,
  });
  const {
    data: {
      results,
      pagination: { totalResults },
    },
    error: projectEntitlementsError,
  } = useProjectsQuery({
    search,
    sorting,
    pagination,
    filters,
    classification: ['production', 'sample'],
  });
  const isSearchApply = search && !!search.trim();
  const isFiltersOrSearchApply = isSearchApply || !!Object.keys(filters).length;
  const {
    canCreateProject,
    showErrorState,
    showEmptyState,
    showLoadingState,
    accountsUserIsAccAccountAdmin,
    accountsUserIsExecutive,
  } = usePageStates({
    projectEntitlementsError,
    results,
    isFiltersOrSearchApply,
  });

  usePerformanceTrackingWithEffects({
    trackingId: PROJECT_LIST_TRACKING_ID,
    loading: showLoadingState,
    displayName: 'Project List',
    parentTrackingId: TOTAL_REMOTE_LOADING_TIME,
  });

  useSetProductEntryFlow(results);

  const columns = useMemo(
    () => [
      platformColumn,
      nameColumn,
      jobNumberColumn,
      toolEntryFlow ? defaultAccessCellColumn : productEntryFlowCellColumn,
      accountNameCellColumn,
      createdAtCellColumn,
    ],
    [toolEntryFlow],
  );

  if (results && !shouldReportView.current) {
    track(AnalyticsEvents.viewProjects, {
      projects_no_in_list: results.length,
      total_projects_no: totalResults,
    });
    shouldReportView.current = true;
  }

  // NOTE: THIS FUNCTION IS POTENTIALLY CALLED _TWICE_
  // This is due to the implementation of the create project modal
  // Code you write here should be resilient to being called twice
  const onSubmitHandler = useCallback(
    (createProjectResponse: CreateProjectSubmitResponse) => {
      if (createProjectResponse.status === 'success') {
        setShowProjectCreateModal(false);
        const { response: project, metadata } =
          createProjectResponse as CreateProjectSubmitSuccess;
        track(AnalyticsEvents.createProjectModalSubmit, {
          project_name: project.name,
          project_id: project.id,
          account_id: project.accountId,
          template_id: metadata && metadata.templateId,
          elapsed_time: Math.floor((Date.now() - modalOpenTime.current) / 1000), // in seconds
        });
      } else {
        //TODO: report error
        //Get the appropriate error message
        const errorMessage = getProjectCreateErrorMessage(
          createProjectResponse,
        );
        showNotification({
          ...ErrorNotificationBaseProps,
          message: <span>{errorMessage}</span>,
        });
      }
    },
    [showNotification],
  );

  const onCloseHandler = useCallback(() => {
    const elapsedTimeInSeconds = Math.floor(
      (Date.now() - modalOpenTime.current) / 1000,
    ); // in seconds
    track(AnalyticsEvents.createProjectModalClose, {
      elapsed_time: elapsedTimeInSeconds,
    });
    setShowProjectCreateModal(false);
  }, []);

  const renderCellHandler = useCallback(
    ({ children, row }) => (
      <TableLink
        project={row}
        onClick={() =>
          trackRowClick({
            analyticName: AnalyticsEvents.projectsNavigateToProject,
            selectedProject: row,
            accountsUserIsAccAccountAdmin,
          })
        }
      >
        {children}
      </TableLink>
    ),
    [accountsUserIsAccAccountAdmin],
  );

  return (
    <ProjectListErrorBoundery>
      <PageWithSearchAndFilters
        {...sideFiltersProps}
        data-testid="ProjectList"
        filters={filters}
        renderSearch={() => (
          <SearchBar
            data-testid="ProjectListSearch"
            style={{ width: 300, marginLeft: 'auto' }}
            placeholder={i18n.t('SEARCH_PROJECTS_OR_NUMBER').toString()}
            value={search}
            onChange={onSearchChange}
            analyticsEvents={{
              searchBarQueryChange: AnalyticsEvents.projectsSearchBarChange,
            }}
          />
        )}
        renderCreateButton={() =>
          canCreateProject && (
            <Button
              data-testid="createProjectButton"
              renderIcon={(p) => <PlusIcon {...p} size={18} />}
              variant={Button.VARIANTS.PRIMARY}
              onClick={() => {
                track(AnalyticsEvents.createProjectModalOpen);
                setShowProjectCreateModal(true);
                modalOpenTime.current = Date.now();
              }}
            >
              {i18n.t('CREATE_PROJECT')}
            </Button>
          )
        }
      >
        <>
          {showErrorState && (
            <HttpErrorState errorMessage={i18n.t('COULD_NOT_LOAD_PROJECTS')} />
          )}
          {showEmptyState && !showErrorState && (
            <ProjectListEmptyState
              accountAdminId={accountsUserIsAccAccountAdmin?.[0]?.id}
              executiveId={accountsUserIsExecutive?.[0]?.id}
              canCreateProject={canCreateProject}
            />
          )}
          {!showEmptyState && !showErrorState && (
            <Table
              {...tableProps}
              loading={showLoadingState}
              data={results || []}
              columns={columns}
              recordCount={totalResults}
              renderCell={renderCellHandler}
            />
          )}
        </>
      </PageWithSearchAndFilters>

      {showProjectCreateModal && (
        <>
          <TabTitle value={i18n.t('CREATE_PROJECT_TITLE')} />
          <CreateProjectModal
            variant={COMPONENT_VARIANTS.PROJECT}
            onSubmit={onSubmitHandler}
            onClose={onCloseHandler}
            mapBoxAccessToken={MAPBOX_ACCESS_TOKEN}
            show={showProjectCreateModal}
          />
        </>
      )}
    </ProjectListErrorBoundery>
  );
};

export default ProjectList;
